import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldStar, ShieldWarning, X, Lock } from "@phosphor-icons/react";

const TIER_META = {
  1: { label: "General", color: "text-nk-mute", bg: "bg-nk-line", ring: "border-nk-line" },
  2: { label: "Ranked", color: "text-nk-accent", bg: "bg-nk-accent/15", ring: "border-nk-accent/30" },
  3: { label: "Top-finisher", color: "text-nk-gold", bg: "bg-nk-gold/15", ring: "border-nk-gold/40" },
  4: { label: "Prize-cleared", color: "text-nk-gold", bg: "bg-nk-gold/20", ring: "border-nk-gold/50" },
};

const ID_TYPES = [
  { v: "passport", l: "Passport" },
  { v: "national_id", l: "National ID" },
  { v: "drivers_licence", l: "Driver's Licence" },
];

/**
 * VerificationCard — sits on the Profile page.
 * Auto-shows the player's current tier (1-4) and explains the next step.
 * If tier ≥ 2, lets the player submit a Tier-3 review request.
 * If tier = 3, lets them submit Tier-4 (prize claim) review.
 */
export default function VerificationCard({ user }) {
  const [info, setInfo] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [targetTier, setTargetTier] = useState(3);

  useEffect(() => {
    api.get("/verification/me").then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, []);

  if (!info) return null;
  const meta = TIER_META[info.tier] || TIER_META[1];
  const tier = info.tier;

  return (
    <>
      <section className="nk-card-gold p-5 rounded-sm" data-testid="verification-card">
        <div className="flex items-center gap-3 mb-3">
          <ShieldStar size={22} weight="duotone" className="text-nk-gold" />
          <p className="eyebrow text-nk-gold">Verification</p>
          <span className={`ml-auto px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${meta.bg} ${meta.color} ${meta.ring}`}
                data-testid={`verification-tier-${tier}`}>
            Tier {tier} · {meta.label}
          </span>
        </div>

        {/* Tier 1 — needs activity */}
        {tier === 1 && (
          <div className="text-sm text-nk-mute space-y-2">
            <p>Play <span className="text-nk-text">{info.games_to_ranked} more games</span> to reach <span className="text-nk-accent">Ranked (Tier 2)</span> and appear on public leaderboards.</p>
            {info.days_to_ranked > 0 && (
              <p className="text-xs">Your account also needs to be ≥ 2 days old (current age: {info.account_age_days}d).</p>
            )}
            <div className="h-1 mt-2 bg-nk-line/40 rounded-sm overflow-hidden">
              <div className="h-full bg-nk-accent" style={{ width: `${Math.min(100, (info.games_played / 10) * 100)}%` }} />
            </div>
          </div>
        )}

        {/* Tier 2 — eligible for Top-finisher review */}
        {tier === 2 && (
          <div className="text-sm space-y-3">
            <p className="text-nk-mute">You're on public leaderboards. To enter the Day-31 championship (Top 10 NG / GH), submit ID for <span className="text-nk-gold">Top-finisher review (Tier 3)</span>.</p>
            <button
              onClick={() => { setTargetTier(3); setShowForm(true); }}
              data-testid="verification-submit-t3"
              className="px-4 py-2 bg-nk-gold text-nk-bg text-xs font-medium rounded-sm hover:bg-nk-goldSoft inline-flex items-center gap-2"
            >
              <ShieldCheck size={14} weight="bold" /> Submit ID for Top-finisher review
            </button>
          </div>
        )}

        {/* Tier 3 — final verified state (Tier 4 not in use for now) */}
        {tier === 3 && (
          <div className="text-sm space-y-2">
            <p className="text-white inline-flex items-center gap-2">
              <ShieldCheck size={16} weight="duotone" className="text-nk-gold" />
              <span>Top-finisher verified.</span>
            </p>
            <p className="text-xs text-white/55">Approved {info.approved_at?.slice(0, 10)}</p>
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-white/8">
          <p className="text-[10px] text-white/45 leading-relaxed">
            The verification ladder protects competitive integrity. Tier 1 (general) → Tier 2 (10 games + 2 days, auto) → Tier 3 (admin reviews ID before championship). We only ask for ID when there&apos;s prize money at stake.
          </p>
        </div>
      </section>

      {showForm && (
        <SubmitModal
          tier={targetTier}
          country={user?.country || "NG"}
          onClose={() => setShowForm(false)}
          onSubmitted={() => { setShowForm(false); toast.success("Submitted — we'll review within 48 hours."); }}
        />
      )}
    </>
  );
}

function SubmitModal({ tier, country, onClose, onSubmitted }) {
  const [f, setF] = useState({
    tier,
    full_legal_name: "",
    id_document_type: "national_id",
    id_document_number: "",
    phone: "",
    residency_country: country,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/verification/request", f);
      onSubmitted();
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="nk-card-gold w-full max-w-md p-5 rounded-sm space-y-3" data-testid="verification-modal">
        <div className="flex items-center justify-between">
          <p className="eyebrow text-nk-gold flex items-center gap-2">
            <Lock size={11} /> Tier {tier} review
          </p>
          <button type="button" onClick={onClose} className="text-nk-mute hover:text-nk-text"><X size={14} /></button>
        </div>

        <p className="text-xs text-nk-mute">
          Your details are stored securely and only used by the NKognito review team. We don't share with third parties.
        </p>

        <Field label="Full legal name *">
          <input value={f.full_legal_name} onChange={(e) => set("full_legal_name", e.target.value)} required
                 data-testid="verif-name"
                 className="w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm outline-none focus:border-nk-gold" />
        </Field>

        <div className="grid grid-cols-3 gap-2">
          {ID_TYPES.map((t) => (
            <button type="button" key={t.v} onClick={() => set("id_document_type", t.v)}
                    className={`px-2 py-2 border rounded-sm text-xs ${f.id_document_type === t.v ? "border-nk-gold bg-nk-gold/10 text-nk-gold" : "border-nk-line text-nk-mute"}`}>
              {t.l}
            </button>
          ))}
        </div>

        <Field label="ID number *">
          <input value={f.id_document_number} onChange={(e) => set("id_document_number", e.target.value)} required
                 data-testid="verif-id"
                 className="w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm outline-none focus:border-nk-gold font-mono" />
        </Field>

        <Field label="Phone (optional)">
          <input value={f.phone} onChange={(e) => set("phone", e.target.value)}
                 placeholder="+234 …"
                 className="w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm outline-none focus:border-nk-gold" />
        </Field>

        <Field label="Residency country *">
          <input value={f.residency_country} onChange={(e) => set("residency_country", e.target.value.toUpperCase())} required maxLength={4}
                 data-testid="verif-residency"
                 className="w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm outline-none focus:border-nk-gold font-mono uppercase" />
        </Field>

        <Field label="Notes (optional)">
          <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} maxLength={500}
                    className="w-full bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm outline-none focus:border-nk-gold" />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-nk-mute">Cancel</button>
          <button type="submit" disabled={busy}
                  data-testid="verif-submit"
                  className="px-4 py-1.5 bg-nk-gold text-nk-bg text-xs font-medium rounded-sm disabled:opacity-40">
            {busy ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <p className="text-[10px] uppercase tracking-wider text-nk-mute mb-1">{label}</p>
      {children}
    </label>
  );
}
